import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Flex, FullscreenTakeover, LoadingIndicator, type Step } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useAppContext } from "~/contexts/appContext";
import {
  DdqProdOpsTaskKind,
  DdqProdOpsTaskState,
  DdqProdOpsWizardStep,
  DdqProdOpsWorkflowType,
} from "~/graphql/supergraphEnums";
import {
  useGetAssignableDdqProdOpsOperatorsQuery,
  useStartDdqProdOpsTaskMutation,
} from "~/graphql/supergraphHooks";
import { SPACING } from "~/styles/variables";
import { toDbId } from "~/utils/graphql-helpers";
import { WIZARD_STEP_LABELS } from "../DDQueue/labels";
import { useDdqProdOpsQueueRow } from "../DDQueue/useDdqProdOpsQueueRow";
import { OriginalFormatWizard } from "./OriginalFormat";
import { EmptyState } from "./States/EmptyState";
import { ErrorState } from "./States/ErrorState";
import { StepNavigation } from "./StepNavigation";
import { ConvertToPdfStep } from "./steps/ConvertToPdfStep";
import { GenerateFirstDraftStep } from "./steps/GenerateFirstDraftStep";
import { ApproveForGpModal } from "./steps/GenerateFirstDraftStep/ApproveForGpModal";
import { EditStructureModal } from "./steps/GenerateFirstDraftStep/EditStructureModal";
import { GenerateFirstDraftModal } from "./steps/GenerateFirstDraftStep/GenerateFirstDraftModal";
import { MarkReadyForReviewModal } from "./steps/GenerateFirstDraftStep/MarkReadyForReviewModal";
import { ReturnForEditsModal } from "./steps/GenerateFirstDraftStep/ReturnForEditsModal";
import { useCheckerHandoff } from "./steps/GenerateFirstDraftStep/useCheckerHandoff";
import { useGenerateDraftRun } from "./steps/GenerateFirstDraftStep/useGenerateDraftRun";
import { StructureDdqStep } from "./steps/StructureDdqStep";
import * as styles from "./styles_vanilla.css";
import { useWizardState } from "./useWizardState";
import type { UseDdqProdOpsQueueRowResult } from "../DDQueue/useDdqProdOpsQueueRow";
import type { RunScope } from "./steps/GenerateFirstDraftStep/useGenerateDraftRun";
import type { StepRendererType } from "./types";
import type { DdqQueueRow, DdqQueueTask } from "../DDQueue/types";

export type WizardProps = {
  targetId: string;
  basePath: string;
};

// If a 3rd, genuinely different workflow type is ever added, this file's ~6 scattered
// workflow-type branch points (this function included) should probably consolidate into a
// strategy-object pattern (e.g. Record<DdqProdOpsWorkflowType, WorkflowStrategy> covering step
// order, the reviewable-step predicate, and footer copy/mutation) rather than growing a 7th.
const stepOrderFor = (
  workflowType: DdqProdOpsWorkflowType | undefined,
): DdqProdOpsWizardStep[] =>
  workflowType === DdqProdOpsWorkflowType.BackfillDdq
    ? [DdqProdOpsWizardStep.UploadPdf, DdqProdOpsWizardStep.StructureDdq]
    : [
        DdqProdOpsWizardStep.UploadPdf,
        DdqProdOpsWizardStep.StructureDdq,
        DdqProdOpsWizardStep.GenerateFirstDraft,
      ];

type CheckerHandoffModal = "markReady" | "returnForEdits" | "approve" | null;

// Reuses the Queue's GetDdqProdOpsQueue rather than a second per-request read.
// targetId is the workflow's target entity (a request, or a historical upload for a backfill) —
// unique among live workflows, so it keys the route; row.id is the workflow, row.ddqId the structure.
// OriginalFormat has no sub-wizard (no PDF conversion, no AI drafting) and needs its own step bar
// and card layout entirely, so it's dispatched to its own component here rather than woven into
// StandardDdqWizard's step machinery below — exactly the "genuinely different workflow type"
// case the comment above anticipates.
export const Wizard = ({ targetId, basePath }: WizardProps) => {
  const history = useHistory();
  const { row, isLoading, error, refetch } = useDdqProdOpsQueueRow(targetId);
  const handleExit = () => history.push(basePath);

  if (row?.workflowType === DdqProdOpsWorkflowType.OriginalFormat) {
    return (
      <OriginalFormatWizard
        row={row}
        isLoading={isLoading}
        error={error}
        refetch={refetch}
        handleExit={handleExit}
      />
    );
  }

  return (
    <StandardDdqWizard
      targetId={targetId}
      row={row}
      isLoading={isLoading}
      error={error}
      refetch={refetch}
      handleExit={handleExit}
    />
  );
};

type StandardDdqWizardProps = UseDdqProdOpsQueueRowResult & {
  targetId: string;
  handleExit: () => void;
};

const StandardDdqWizard = ({
  targetId,
  row,
  isLoading,
  error,
  refetch,
  handleExit,
}: StandardDdqWizardProps) => {
  const processTask = row?.tasks.find((task) => task.kind === DdqProdOpsTaskKind.Process);
  const reviewTask = row?.tasks.find((task) => task.kind === DdqProdOpsTaskKind.Review);

  // currentStep is only set once a task starts; sourceFileIsPdf decides the step before that.
  const notStartedStep = row?.sourceFileIsPdf
    ? DdqProdOpsWizardStep.StructureDdq
    : DdqProdOpsWizardStep.UploadPdf;
  // Cast: codegen types currentStep against a separate string union, not this enum.
  const storedStep = processTask?.currentStep as DdqProdOpsWizardStep | undefined;
  // A stored GenerateFirstDraft cursor is stale once there's no structure behind it — nothing in
  // the real extraction/generation path reliably writes this column. Landing there throws with no
  // way back (disableStepLinks), so refuse to trust it without row.ddqId.
  const canTrustGenerateFirstDraft = row?.ddqId != null;
  const effectiveStep =
    storedStep === DdqProdOpsWizardStep.GenerateFirstDraft && !canTrustGenerateFirstDraft
      ? undefined
      : storedStep;

  // Lifted here, not into either step: Structure's trigger and Generate-first-draft's own re-run
  // triggers all hit the same mutation against the same task, so there's exactly one hook
  // instance and modal. onStarted closes over currentStep/goToStep, declared below — fine, since
  // it only runs after the mutation resolves, well after this render assigns them.
  const [modalScope, setModalScope] = useState<RunScope | null>(null);
  const closeModal = () => setModalScope(null);

  // Only navigates back to Structure DDQ — the actual re-run/clear action lives there already.
  const [isEditStructureModalOpen, setIsEditStructureModalOpen] = useState(false);

  const {
    defaults,
    isLoadingDefaults,
    isRunning,
    hasEverRun,
    isRunStateKnown,
    failureMessage,
    startError,
    isStarting,
    latestSucceededAt,
    run,
  } = useGenerateDraftRun({
    arenaId: row ? toDbId(row.arena.id) : undefined,
    ddqProdOpsTaskId: processTask?.id,
    onStarted: () => {
      closeModal();
      // Starting from the Structure step advances the wizard client-side, the same way
      // ConvertToPdfStep's onComplete does — the server's own currentStep isn't waited on.
      if (currentStep === DdqProdOpsWizardStep.StructureDdq) {
        goToStep(DdqProdOpsWizardStep.GenerateFirstDraft);
      }
    },
    onSucceeded: () => {},
  });

  // State, not who's viewing: Process reaching Done hands off to the checker regardless of who's
  // looking (matches the backend, which never checks that the caller is the assignee). hasEverRun
  // separately keeps routing here on later visits instead of back to Structure DDQ — the maker
  // needs to see/edit existing answers, not regenerate from scratch.
  const isBackfill = row?.workflowType === DdqProdOpsWorkflowType.BackfillDdq;
  // Backfill has no Generate First Draft step to hand off on, so its reviewable step is Structure
  // DDQ instead — same override, same underlying reason as above (currentStep isn't reliable
  // enough to land a fresh checker on the right step by itself), just without a hasEverRun
  // equivalent, since there's no generation run for backfill to have ever had.
  const derivedStep = isBackfill
    ? processTask?.state === DdqProdOpsTaskState.Done
      ? DdqProdOpsWizardStep.StructureDdq
      : (effectiveStep ?? notStartedStep)
    : processTask?.state === DdqProdOpsTaskState.Done || hasEverRun
      ? DdqProdOpsWizardStep.GenerateFirstDraft
      : (effectiveStep ?? notStartedStep);

  const { currentStep, goToStep } = useWizardState(derivedStep);

  // Same "lift to wizard level" reasoning as generation: the trigger lives in the footer
  // (structurally a sibling of Body, so it can't live inside either step's own render tree).
  const [checkerHandoffModal, setCheckerHandoffModal] = useState<CheckerHandoffModal>(null);
  const closeCheckerHandoffModal = () => setCheckerHandoffModal(null);

  const {
    onMarkReadyForReview,
    isMarkingReady,
    markReadyError,
    onApproveForGp,
    isApproving,
    approveError,
    onCompleteBackfill,
    isCompletingBackfill,
    completeBackfillError,
    onReturnForEdits,
    isReturning,
    returnError,
  } = useCheckerHandoff({
    processTaskId: processTask?.id,
    reviewTaskId: reviewTask?.id,
    // Marking ready for review completes Process — the maker has nothing left to do here, and
    // per isChecker/isMaker being state- not identity-gated, staying would now show them the
    // checker's own footer (Return for edits / Approve for GP), which isn't theirs to use.
    onMarkedReady: () => {
      closeCheckerHandoffModal();
      handleExit();
    },
    // Approving (or completing a backfill review) finishes Review — same reasoning as
    // onReturned below, the checker has nothing left to do on this page.
    onApproved: () => {
      closeCheckerHandoffModal();
      handleExit();
    },
    // Returning for edits reopens both tasks for the maker — the checker has nothing left to do
    // on this page, so exit back to the queue rather than leaving them stranded on a stale view.
    onReturned: () => {
      closeCheckerHandoffModal();
      handleExit();
    },
  });

  const visibleSteps = stepOrderFor(
    row?.workflowType as DdqProdOpsWorkflowType | undefined,
  ).filter((step) => step !== DdqProdOpsWizardStep.UploadPdf || !row?.sourceFileIsPdf);
  const currentIndex = visibleSteps.indexOf(currentStep);
  const steps: Step[] = visibleSteps.map((step, index) => ({
    name: WIZARD_STEP_LABELS[step],
    url: step,
    status: step === currentStep ? "active" : index < currentIndex ? "complete" : "inactive",
    hidden: false,
    index,
  }));

  const buildStepRenderers = (
    loadedRow: DdqQueueRow,
    loadedProcessTask: DdqQueueTask,
  ): StepRendererType => ({
    [DdqProdOpsWizardStep.UploadPdf]: () => (
      <ConvertToPdfStep
        targetId={targetId}
        onComplete={() => goToStep(DdqProdOpsWizardStep.StructureDdq)}
      />
    ),
    [DdqProdOpsWizardStep.StructureDdq]: () => (
      <StructureDdqStep
        arenaId={toDbId(loadedRow.arena.id)}
        processTaskId={loadedProcessTask.id}
        ddqId={loadedRow.ddqId}
        workflowType={loadedRow.workflowType as DdqProdOpsWorkflowType}
        uploadId={loadedRow.targetId}
        completedDdqTitle={
          loadedRow.workflowType === DdqProdOpsWorkflowType.BackfillDdq ? loadedRow.title : null
        }
        onExtractionSucceeded={refetch}
      />
    ),
    [DdqProdOpsWizardStep.GenerateFirstDraft]: () => (
      <GenerateFirstDraftStep
        arenaId={toDbId(loadedRow.arena.id)}
        ddqProdOpsTaskId={loadedProcessTask.id}
        isRunning={isRunning}
        hasEverRun={hasEverRun}
        failureMessage={failureMessage}
        isRunStateKnown={isRunStateKnown}
        latestSucceededAt={latestSucceededAt}
        onRerunWhole={() => setModalScope({ kind: "whole" })}
        onRerunSection={(sectionId, sectionTitle) =>
          setModalScope({ kind: "section", sectionId, sectionTitle })
        }
        // Hidden for now — re-enable with isMaker ? () => setIsEditStructureModalOpen(true) : undefined
        onEditStructure={undefined}
      />
    ),
  });

  const renderBody = () => {
    if (isLoading) {
      return <LoadingIndicator size="large" data-testid="ddq-wizard-loading-indicator" />;
    }
    // Only `tasks.assignee` can null-propagate (see useDdqProdOpsQueueRow), so an error
    // alongside a successfully loaded row is a partial failure elsewhere in the queue —
    // it shouldn't block processing this request.
    if (error && !row) {
      return <ErrorState onExit={handleExit} onRetry={refetch} errorMessage={error.message} />;
    }
    if (!row || !processTask) {
      return <EmptyState onExit={handleExit} />;
    }
    return buildStepRenderers(row, processTask)[currentStep]?.() ?? null;
  };

  const isOnGenerateFirstDraft = currentStep === DdqProdOpsWizardStep.GenerateFirstDraft;
  const isOnBackfillReviewableStep =
    isBackfill && currentStep === DdqProdOpsWizardStep.StructureDdq;
  const isReviewableStep = isOnGenerateFirstDraft || isOnBackfillReviewableStep;
  // State, not who's viewing — Process reaching Done is what makes it the checker's turn, and
  // reopening to Pending (returnDdqForEdits) is what makes it the maker's turn again. Backfill
  // reuses this exact rule on the Structure DDQ step instead of Generate First Draft, since it
  // has no Generate First Draft step to hand off on.
  const isChecker =
    isReviewableStep &&
    processTask?.state === DdqProdOpsTaskState.Done &&
    reviewTask?.state !== DdqProdOpsTaskState.Done;
  const isMaker =
    isReviewableStep && !isChecker && processTask?.state !== DdqProdOpsTaskState.Done;

  // markDdqReadyForReview only assigns the review task, never starts it, and there's no separate
  // "Start review" affordance — so landing here as the checker is what starts it.
  const [startReviewTask] = useStartDdqProdOpsTaskMutation({
    context: withClientContext("federation"),
    refetchQueries: ["GetDdqProdOpsQueue"],
  });
  // reviewTask.state only changes once this resolves (refetchQueries updates it alongside), so
  // the dependency array alone keeps this from firing twice while the request is in flight.
  useEffect(() => {
    if (isChecker && reviewTask?.state === DdqProdOpsTaskState.Pending) {
      startReviewTask({ variables: { taskId: reviewTask.id } }).catch(() => {
        // Surfaces naturally: a checker action against a still-pending task fails with its own
        // server error, same as it did before this effect existed.
      });
    }
  }, [isChecker, reviewTask?.state, reviewTask?.id, startReviewTask]);

  // No dedicated checker role exists — the assignable pool is the same one the maker was drawn
  // from. "Assign reviewer" only reads as a real handoff when someone else is actually in it;
  // otherwise "Review" fits better. toDbId decodes the relay id to compare against the plain id
  // useAppContext stores.
  const { user } = useAppContext();
  const { data: assignableOperatorsData } = useGetAssignableDdqProdOpsOperatorsQuery({
    context: withClientContext("federation"),
    // network-only: this is a point-in-time eligibility peek, not something that should keep
    // showing a stale answer from whenever it was first cached.
    fetchPolicy: "network-only",
    skip: !isMaker,
  });
  const hasOtherEligibleChecker = (
    assignableOperatorsData?.assignableDdqProdOpsOperators ?? []
  ).some((operator) => toDbId(operator.id) !== user.id);
  // Same gate QuestionTree/the generation trigger use: a structure must exist first. Never
  // applies to backfill — it has no Generate First Draft footer state, going straight from
  // maker to checker on this same step.
  const showGenerateFirstDraftFooter =
    !isBackfill && currentStep === DdqProdOpsWizardStep.StructureDdq && row?.ddqId != null;

  const footerContent = showGenerateFirstDraftFooter ? (
    <Flex justifyContent="flex-end" className={styles.footerBar}>
      <Button
        onClick={() => setModalScope({ kind: "whole" })}
        data-testid="ddq-structure-generate-first-draft"
      >
        Generate first draft
      </Button>
    </Flex>
  ) : isMaker ? (
    <Flex justifyContent="flex-end" className={styles.footerBar}>
      <Button
        onClick={() => setCheckerHandoffModal("markReady")}
        data-testid="ddq-mark-ready-for-review-trigger"
      >
        {hasOtherEligibleChecker ? "Assign reviewer" : "Review"}
      </Button>
    </Flex>
  ) : isChecker ? (
    <Flex justifyContent="flex-end" gap={SPACING.SMALL} className={styles.footerBar}>
      <Button
        variant="neutral"
        onClick={() => setCheckerHandoffModal("returnForEdits")}
        data-testid="ddq-return-for-edits-trigger"
      >
        Return for edits
      </Button>
      <Button
        onClick={() => setCheckerHandoffModal("approve")}
        data-testid="ddq-approve-for-gp-trigger"
      >
        {isBackfill ? "Approve" : "Approve for GP"}
      </Button>
    </Flex>
  ) : null;

  return (
    <>
      <FullscreenTakeover>
        <FullscreenTakeover.Header onExit={handleExit} title={row?.title ?? "Process DDQ"} />
        <FullscreenTakeover.Subheader>
          <StepNavigation steps={steps} />
        </FullscreenTakeover.Subheader>
        <FullscreenTakeover.Body>{renderBody()}</FullscreenTakeover.Body>
        {footerContent && (
          <FullscreenTakeover.Footer>{footerContent}</FullscreenTakeover.Footer>
        )}
      </FullscreenTakeover>
      {modalScope && (
        <GenerateFirstDraftModal
          scope={modalScope}
          defaults={defaults}
          isLoadingDefaults={isLoadingDefaults}
          errorMessage={startError}
          isSubmitting={isStarting}
          onHide={closeModal}
          onConfirm={(prompt) => run(prompt, modalScope)}
        />
      )}
      {checkerHandoffModal === "markReady" && (
        <MarkReadyForReviewModal
          errorMessage={markReadyError}
          isSubmitting={isMarkingReady}
          onHide={closeCheckerHandoffModal}
          onConfirm={onMarkReadyForReview}
        />
      )}
      {checkerHandoffModal === "returnForEdits" && (
        <ReturnForEditsModal
          makerName={processTask?.assignee?.fullName ?? undefined}
          errorMessage={returnError}
          isSubmitting={isReturning}
          onHide={closeCheckerHandoffModal}
          onConfirm={onReturnForEdits}
        />
      )}
      {checkerHandoffModal === "approve" && (
        <ApproveForGpModal
          variant={isBackfill ? "backfill" : "ddqRequest"}
          owners={row?.owners ?? []}
          errorMessage={isBackfill ? completeBackfillError : approveError}
          isSubmitting={isBackfill ? isCompletingBackfill : isApproving}
          onHide={closeCheckerHandoffModal}
          onConfirm={isBackfill ? onCompleteBackfill : onApproveForGp}
        />
      )}
      {isEditStructureModalOpen && (
        <EditStructureModal
          onHide={() => setIsEditStructureModalOpen(false)}
          onConfirm={() => {
            setIsEditStructureModalOpen(false);
            goToStep(DdqProdOpsWizardStep.StructureDdq);
          }}
        />
      )}
    </>
  );
};
